import { useState, useEffect } from "react";
import { Receipt, CheckCircle2 } from "lucide-react";
import BuySellChargesTable from "./BuySellChargesTable";
import SchedulesForm from "./SchedulesForm";
import FreeDaysForm from "./FreeDaysForm";
import RemarksForm from "./RemarksForm";

const ChargesSection = ({
  formikData,
  chargeList,
  portList,
  carrierList,
  exchangeRates,
  onAddContainer,
  onRemoveContainer,
  complete,
  readOnly,
}) => {
  const [activeTab, setActiveTab] = useState("buy");
  const { values, errors, submitCount } = formikData;

  const TABS = [
    { key: "buy", label: "Buy Charges", count: values.buyCharges?.filter((r) => r.charge).length, hasError: !!errors?.buyCharges },
    { key: "sell", label: "Sell Charges", count: values.sellCharges?.filter((r) => r.charge).length, hasError: !!errors?.sellCharges },
    { key: "schedules", label: "Schedules", count: values.schedules?.length, hasError: !!errors?.schedules },
    { key: "freedays", label: "Free days", count: values.freeDays?.length },
    { key: "remarks", label: "Remarks & Inclusions" },
  ];

  // Buy/Sell/Freight fields only exist in the DOM while their tab is active,
  // so a validation error on a tab the KAM isn't looking at renders no
  // visible ErrorMessage — Submit just silently does nothing. Jump to the
  // first tab with an error whenever a submit attempt is made (submitCount
  // increments on every attempt, pass or fail) so the failure is visible.
  useEffect(() => {
    if (!submitCount) return;
    const firstErrored = TABS.find((t) => t.hasError);
    if (firstErrored) setActiveTab(firstErrored.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  return (
    <div
      id="charges-section"
      className="bg-white rounded-2xl overflow-hidden transition-shadow hover:shadow-md"
      style={{ border: "1px solid #EDEEF1", boxShadow: "0 1px 3px rgba(16,24,40,.05)" }}
    >
      <div
        className="flex items-center gap-3 flex-wrap px-5 py-4"
        style={{ borderBottom: "1px solid #F1F2F5", background: "#FAFAFB" }}
      >
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-none"
          style={{ background: complete ? "#E7F8EF" : "#FFF3D6" }}
        >
          {complete ? (
            <CheckCircle2 size={18} style={{ color: "#12B76A" }} />
          ) : (
            <Receipt size={17} style={{ color: "#B8860B" }} />
          )}
        </span>
        <div
          className="flex items-center gap-1 flex-wrap p-1 rounded-xl oc-scroll"
          style={{ background: "#F1F2F5", overflowX: "auto" }}
        >
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-1.5 rounded-lg text-sm px-4 py-2 transition-colors flex-none"
                style={{
                  background: active ? "#fff" : "transparent",
                  color: active ? "#1f2937" : "#667085",
                  fontWeight: active ? 700 : 600,
                  boxShadow: active ? "0 1px 3px rgba(16,24,40,.1)" : "none",
                }}
              >
                {t.label}
                {!!t.count && (
                  <span
                    className="text-[10px] font-bold rounded-full px-1.5 py-0.5"
                    style={{
                      background: active ? "#FFF3D6" : "#E7E9EC",
                      color: active ? "#B8860B" : "#667085",
                    }}
                  >
                    {t.count}
                  </span>
                )}
                {t.hasError && (
                  <span
                    title="This tab has a field that needs fixing before you can submit"
                    className="w-2 h-2 rounded-full flex-none"
                    style={{ background: "#EF4444" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5">
        {activeTab === "buy" && (
          <BuySellChargesTable
            formikData={formikData}
            tabKey="buyCharges"
            chargeList={chargeList}
            exchangeRates={exchangeRates}
            onAddContainer={onAddContainer}
            onRemoveContainer={onRemoveContainer}
            readOnly={readOnly}
          />
        )}
        {activeTab === "sell" && (
          <BuySellChargesTable
            formikData={formikData}
            tabKey="sellCharges"
            chargeList={chargeList}
            exchangeRates={exchangeRates}
            onAddContainer={onAddContainer}
            onRemoveContainer={onRemoveContainer}
            readOnly={readOnly}
          />
        )}
        {activeTab === "schedules" && (
          <SchedulesForm formikData={formikData} portList={portList} carrierList={carrierList} readOnly={readOnly} />
        )}
        {activeTab === "freedays" && <FreeDaysForm formikData={formikData} readOnly={readOnly} />}
        {activeTab === "remarks" && <RemarksForm readOnly={readOnly} />}
      </div>
    </div>
  );
};

export default ChargesSection;
